'use client';

import Alert from '@enlighten/design-system/dist/Alert';
import Button from '@enlighten/design-system/dist/Button';
import Dialog from '@enlighten/design-system/dist/Dialog';
import Stack from '@enlighten/design-system/dist/Stack';
import type { PropsWithChildren } from 'react';
import { createContext, useContext, useState } from 'react';

interface AlertValue {
	type: 'success' | 'info' | 'warning' | 'error';
	title: string;
	subTitle: string;
	onOk?(): void;
	onCancel?(): void;
	okText?: string;
	cancelText?: string;
}

const alertContext = createContext<AlertValue | null | undefined>(null);
const onChangeAlertContext = createContext<(value: AlertValue | null | undefined) => void>(() => {});

export function useAlert() {
	return useContext(alertContext);
}

/**
 * @function useOnChangeMessage Alert 팝업 노출 함수
 * @argument type `'success' | 'info' | 'warning' | 'error'`
 * @argument message Alert 팝업 내용
 * @argument duration Alert 팝업 노출 시간
 */
export function useOnChangeAlert() {
	return useContext(onChangeAlertContext);
}

/** Alert 팝업 Provider */
export default function AlertProvider({ children }: PropsWithChildren) {
	const [alert, setAlert] = useState<AlertValue | null | undefined>();
	const [visible, setVisible] = useState<boolean>(false);

	return (
		<alertContext.Provider value={alert}>
			<onChangeAlertContext.Provider
				value={(value) => {
					setAlert(value);
					setVisible(!!value);
				}}
			>
				{children}
				<Dialog
					open={visible}
					PaperProps={{
						sx: {
							maxWidth: 416,
							width: '100%',
							margin: '0 8px',
							padding: 3,
						},
					}}
					sx={{
						'.MuiDialog-container': {
							alignItems: 'baseline',
							paddingY: '100px',
						},
					}}
				>
					<Alert
						severity={alert?.type ?? 'info'}
						variant="outlined"
						title={alert?.title}
						subTitle={alert?.subTitle}
						sx={{
							border: 'none',
							borderRadius: '6px',
							marginBottom: 3,
							'.MuiAlert-message': {
								display: 'flex',
								flexDirection: 'column',
								gap: 1,
							},
						}}
					/>
					<Stack direction="row" justifyContent="flex-end" gap={2}>
						{alert?.onOk && (
							<Button
								variant="outlined"
								onClick={() => {
									if (alert && alert.onCancel) {
										alert.onCancel();
									} else {
										setVisible(false);
										setTimeout(() => setAlert(undefined), 500);
									}
								}}
							>
								{alert?.cancelText ?? '취소'}
							</Button>
						)}
						<Button
							onClick={() => {
								if (alert && alert.onOk) {
									alert.onOk();
								} else {
									setVisible(false);
									setTimeout(() => setAlert(undefined), 500);
								}
							}}
						>
							{alert?.okText ?? '확인'}
						</Button>
					</Stack>
				</Dialog>
			</onChangeAlertContext.Provider>
		</alertContext.Provider>
	);
}
